import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { Reveal, MaskedLines, Overline, Magnetic } from "@/components/Motion";

const POSITIONS = ["Architect", "Junior Architect", "Architectural Intern", "Interior Designer", "Artist Collaborator", "Other"];

const SOFTWARES = [
  "AutoCAD", "Revit", "SketchUp", "Rhino", "Lumion / Enscape / D5",
  "Photoshop", "Illustrator", "InDesign", "Blender", "V-Ray",
];

function FileField({ label, file, onPick, inputRef, testid }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-2">{label}</p>
      <button
        type="button"
        data-testid={testid}
        onClick={() => inputRef.current.click()}
        className={`w-full border border-dashed px-5 py-6 text-left transition-colors flex items-center gap-4 ${
          file ? "border-brand/60 bg-brand/5" : "border-ink/25 hover:border-brand"
        }`}
      >
        {file ? <Check size={16} className="text-brand shrink-0" /> : <Upload size={16} className="text-ink/40 shrink-0" />}
        <span className={`text-sm truncate ${file ? "text-ink" : "text-ink/40"}`}>
          {file ? file.name : "Choose PDF — max 10 MB"}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.type !== "application/pdf") {
            toast.error(`${label}: PDF only.`);
            e.target.value = "";
            return;
          }
          if (f.size > 10 * 1024 * 1024) {
            toast.error(`${label}: please keep it under 10 MB.`);
            e.target.value = "";
            return;
          }
          onPick(f);
        }}
      />
    </div>
  );
}

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "Architectural Intern", otherSoftware: "", message: "" });
  const [software, setSoftware] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const portfolioRef = useRef(null);
  const cvRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSoftware = (sname) =>
    setSoftware((prev) => (prev.includes(sname) ? prev.filter((x) => x !== sname) : [...prev, sname]));

  const submit = async (e) => {
    e.preventDefault();
    if (!portfolio || !cv) {
      toast.error("Please attach both your portfolio and CV (PDF).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("software", [...software, form.otherSoftware].filter(Boolean).join(", "));
      fd.append("portfolio", portfolio);
      fd.append("cv", cv);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application sent — thank you.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your application. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-ink/25 focus:border-brand outline-none py-3 text-base placeholder:text-ink/30 transition-colors";

  return (
    <main data-testid="careers-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Link to="/contact" data-testid="careers-back" className="link-line inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60">
        <ArrowLeft size={14} /> Contact
      </Link>
      <div className="mt-8">
        <Overline>Careers</Overline>
        <MaskedLines
          className="mt-6"
          lineClassName="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight"
          lines={["Make things with us."]}
        />
        <Reveal className="mt-8 max-w-2xl">
          <p className="text-ink/60 leading-relaxed">
            IKSHA is a small, hands-on studio in Mangalore working across buildings, interiors, art,
            material research and workshops. If you draw, make, and care about material — write to us.
            Attach your portfolio and CV as separate PDFs; every application is read.
          </p>
        </Reveal>
      </div>

      {done ? (
        <Reveal className="mt-16 border border-brand/40 bg-brand/5 p-10 text-center max-w-2xl" >
          <span className="w-3 h-3 rounded-full bg-brand inline-block" />
          <h2 className="font-display text-3xl tracking-tight mt-5">Application received.</h2>
          <p className="text-ink/60 mt-3 leading-relaxed">
            Thank you — your portfolio and CV have reached the studio. We review every application
            and will write back if there is a fit.
          </p>
        </Reveal>
      ) : (
        <Reveal className="mt-16 max-w-3xl">
          <form onSubmit={submit} data-testid="careers-form" className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <input required data-testid="careers-name" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
              <input required type="email" data-testid="careers-email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
              <input data-testid="careers-phone" value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={inputCls} />
              <select data-testid="careers-position" value={form.position} onChange={set("position")} className={`${inputCls} cursor-pointer`}>
                {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-4">Software knowledge — select all that apply</p>
              <div className="flex flex-wrap gap-2" data-testid="careers-software">
                {SOFTWARES.map((sname) => (
                  <button
                    key={sname}
                    type="button"
                    data-testid={`software-${sname.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleSoftware(sname)}
                    className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                      software.includes(sname)
                        ? "bg-ink text-paper border-ink"
                        : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
                    }`}
                  >
                    {sname}
                  </button>
                ))}
              </div>
              <input
                data-testid="careers-other-software"
                value={form.otherSoftware}
                onChange={set("otherSoftware")}
                placeholder="Other software (optional)"
                className={`${inputCls} mt-5`}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FileField label="Portfolio (PDF)" file={portfolio} onPick={setPortfolio} inputRef={portfolioRef} testid="careers-portfolio" />
              <FileField label="CV / Resume (PDF)" file={cv} onPick={setCv} inputRef={cvRef} testid="careers-cv" />
            </div>

            <textarea rows={4} data-testid="careers-message" value={form.message} onChange={set("message")}
              placeholder="A few lines about you and the kind of work you want to make…" className={`${inputCls} resize-none`} />

            <Magnetic>
              <button type="submit" disabled={busy} data-testid="careers-submit-btn"
                className="bg-ink text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors disabled:opacity-50">
                {busy ? "Sending…" : "Send Application"}
              </button>
            </Magnetic>
          </form>
        </Reveal>
      )}
    </main>
  );
}
